import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  fetchNotificationSettings,
  updateNotificationSetting,
} from "../../../../services/adminMonitorService";

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function NotificationSettings() {
  const [settings, setSettings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [search, setSearch]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchNotificationSettings();
      setSettings(data.settings || []);
    } catch {
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleToggle(customerId, currentEnabled) {
    setTogglingId(customerId);
    try {
      await updateNotificationSetting(customerId, !currentEnabled);
      setSettings((prev) =>
        prev.map((s) =>
          s.customer_id === customerId
            ? { ...s, enabled: !currentEnabled, updated_at: new Date().toISOString() }
            : s
        )
      );
      toast.success(
        !currentEnabled
          ? "Slack notifications enabled for this customer"
          : "Slack notifications disabled for this customer"
      );
    } catch {
      toast.error("Failed to update setting");
    } finally {
      setTogglingId(null);
    }
  }

  const enabledCount = settings.filter((s) => s.enabled).length;
  const filtered = search.trim()
    ? settings.filter((s) => s.customer_name?.toLowerCase().includes(search.trim().toLowerCase()))
    : settings;

  return (
    <div style={{
      background: "#fff", border: "1px solid #e8eaf0",
      borderRadius: 10, padding: "18px 22px",
      boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
    }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#222", letterSpacing: 0.3 }}>
              SLACK NOTIFICATION TRACKING
            </span>
            <span style={{
              fontSize: 10, background: "#e8f0fe", color: "#1a56db",
              borderRadius: 4, padding: "2px 7px", fontWeight: 600,
            }}>
              {enabledCount} of {settings.length} enabled
            </span>
          </div>
          <p style={{ fontSize: 12, color: "#888", margin: 0 }}>
            When enabled for a customer, Slack alerts fire when their transactions are initiated, completed, or fail.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            fontSize: 11, padding: "5px 12px", border: "1px solid #e0e0e0",
            borderRadius: 4, background: "#fff", cursor: loading ? "default" : "pointer",
            color: "#555", display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
          }}
        >
          <span style={{ display: "inline-block", animation: loading ? "spin 1s linear infinite" : "none" }}>↻</span>
          Refresh
        </button>
      </div>

      {/* Info banner */}
      {/* <div style={{
        background: "#fffbeb", border: "1px solid #fde68a",
        borderRadius: 6, padding: "8px 12px", marginBottom: 16,
        display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#92400e",
      }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>ℹ️</span>
        <span>
          Requires <code style={{ background: "#fef3c7", padding: "0 4px", borderRadius: 3 }}>ENABLE_TRANSACTION_NOTIFIER=true</code> and{" "}
          <code style={{ background: "#fef3c7", padding: "0 4px", borderRadius: 3 }}>SLACK_HOOK_URL</code> set in the backend environment.
          The notifier polls every 30 seconds and only fires for customers enabled here.
        </span>
      </div> */}

      {/* Search */}
      <div style={{ marginBottom: 14 }}>
        <input
          type="text"
          placeholder="Search customers…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%", maxWidth: 300, padding: "6px 10px",
            border: "1px solid #e0e0e0", borderRadius: 4, fontSize: 12,
            outline: "none", boxSizing: "border-box",
          }}
        />
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#bbb", fontSize: 13 }}>
          Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#bbb", fontSize: 13 }}>
          {search ? "No customers match your search" : "No customers found"}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#f8f8f8" }}>
              <th style={thStyle}>Customer</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Slack Notifications</th>
              <th style={thStyle}>Last Changed</th>
              <th style={thStyle}>Changed By</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => {
              const isToggling = togglingId === row.customer_id;
              return (
                <tr key={row.customer_id} style={{ borderBottom: "1px solid #f5f5f5" }}>
                  {/* Customer name */}
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500, color: "#222" }}>{row.customer_name}</div>
                    <div style={{ fontSize: 10, color: "#aaa", marginTop: 1 }}>ID #{row.customer_id}</div>
                  </td>

                  {/* Toggle */}
                  <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                      onClick={() => handleToggle(row.customer_id, row.enabled)}
                      disabled={isToggling}
                      title={row.enabled ? "Click to disable" : "Click to enable"}
                      style={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        width: 42,
                        height: 22,
                        borderRadius: 11,
                        border: "none",
                        cursor: isToggling ? "default" : "pointer",
                        background: isToggling
                          ? "#ccc"
                          : row.enabled ? "#2e7d32" : "#bdbdbd",
                        transition: "background 0.2s",
                        padding: 0,
                        outline: "none",
                        opacity: isToggling ? 0.7 : 1,
                      }}
                    >
                      <span style={{
                        position: "absolute",
                        left: row.enabled ? 22 : 2,
                        width: 18, height: 18,
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        transition: "left 0.2s",
                      }} />
                    </button>
                    <div style={{ fontSize: 10, color: row.enabled ? "#2e7d32" : "#aaa", marginTop: 4, fontWeight: 500 }}>
                      {isToggling ? "Saving…" : row.enabled ? "Enabled" : "Disabled"}
                    </div>
                  </td>

                  {/* Last changed */}
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, color: "#666" }}>
                      {formatDate(row.updated_at) || <span style={{ color: "#ccc" }}>—</span>}
                    </span>
                  </td>

                  {/* Changed by */}
                  <td style={tdStyle}>
                    <span style={{ fontSize: 11, color: "#666" }}>
                      {row.updated_by_email || <span style={{ color: "#ccc" }}>—</span>}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
}

const thStyle = {
  padding: "8px 12px", textAlign: "left",
  fontSize: 11, fontWeight: 600, color: "#555",
  borderBottom: "1px solid #eeeeee", whiteSpace: "nowrap",
};
const tdStyle = { padding: "10px 12px", verticalAlign: "middle" };
